import * as THREE from 'three';
import type { MainScene } from '../../scenes';
import type { PlanetSurfaceScene, Scene, StationInteriorScene } from '../../scenes';
import { PLANET_BARRIER_RADIUS } from '../../scenes';
import type { CameraRig } from '../../camera';
import { FollowController } from '../../camera';
import type { InputManager } from '../../input';
import type {
  FadeOverlay,
  PromptLayer,
  MinimapLayer,
  MinimapBlip,
  MinimapCelestial,
  MinimapCelestialKind,
  CrosshairLayer,
  ShipStatsHUD,
  AstronautStatsHUD,
  InventoryOverlay,
  ShopOverlay,
  MissionOverlay,
  SystemMapOverlay,
  WaypointHUD,
  WaypointTarget,
  JumpHud,
  MiningToolHUD,
  UnderwaterOverlay,
  RescueStatusHud,
  PauseMenuOverlay
} from '../../ui';
import type { MapBody } from '../../scenes';
import { JumpController } from '../jump';
import { Planet, Moon, Station } from '../sector';
import { RescueService } from '../rescue';
import type { RescueState } from '../rescue';
import type { SurfaceSavePayload } from '../save';
import { WarpStreaks } from '../../visual';
import type { AudioManager } from '../../audio';
import { gameConfig } from '../../config';
import { FirstPersonController, DEFAULT_FPS_CONFIG } from '../astronaut';
import { SurfaceShipController, DEFAULT_SURFACE_SHIP_CONFIG } from '../ships';
import { FpsMiningTool } from '../mining';
import type { Inventory } from '../inventory';
import type { ShipUpgrades } from '../upgrades';
import type { Landable } from '../sector';
import type { SurfaceSkyConfig } from '../../scenes';
import type { SkyBody } from '../../visual';
import type { GameModeId } from './GameMode';
import { withFade } from './Transition';
import { palette } from '../../visual';
import type { StationCounter } from '../station';
import type {
  MissionEventBus,
  MissionManager,
  MissionStateSnapshot,
  MissionWaypointToken
} from '../missions';
import { MissionSurfaceBeacon } from '../missions';
import {
  buildReturnUrl,
  buildVibejamExitUrl
} from '../portals';
import type { PortalContext } from '../portals';

export interface GameDirectorDependencies {
  readonly spaceScene: MainScene;
  readonly surfaceScene: PlanetSurfaceScene;
  readonly stationScene: StationInteriorScene;
  readonly cameraRig: CameraRig;
  readonly input: InputManager;
  readonly fadeOverlay: FadeOverlay;
  readonly promptLayer: PromptLayer;
  readonly minimapLayer: MinimapLayer;
  readonly crosshairLayer: CrosshairLayer;
  readonly shipStatsHUD: ShipStatsHUD;
  readonly astronautStatsHUD: AstronautStatsHUD;
  readonly inventoryOverlay: InventoryOverlay;
  readonly shopOverlay: ShopOverlay;
  readonly missionOverlay: MissionOverlay;
  readonly systemMapOverlay: SystemMapOverlay;
  readonly waypointHUD: WaypointHUD;
  readonly jumpHud: JumpHud;
  readonly audio: AudioManager;
  readonly fpsInventory: Inventory;
  readonly shipUpgrades: ShipUpgrades;
  readonly miningToolHUD: MiningToolHUD;
  readonly underwaterOverlay: UnderwaterOverlay;
  readonly rescueStatusHud: RescueStatusHud;
  readonly pauseMenuOverlay: PauseMenuOverlay;
  readonly missionManager: MissionManager;
  readonly missionBus: MissionEventBus;
  /** Vibejam portal context — used to figure out where to send the
   *  player when they enter the start portal. Optional; null/undefined
   *  means the player did not arrive via a portal. */
  readonly portalContext?: PortalContext | null;
}

const LANDING_DISTANCE_MARGIN = 140;
const ENTER_SHIP_RANGE = 5.2;
const STATION_APPROACH_EXTRA = 60;
const STATION_EXIT_REACH = 3.2;
const SHOP_INTERACT_RANGE = 3.8;
const MISSION_INTERACT_RANGE = 5.8;
// Combat-scale radar — wide enough to pre-warn the pilot of incoming
// guards before they're in firing range, narrow enough that distant
// patrols around other planets don't clutter the disc.
const SPACE_MINIMAP_RADIUS = 1500;

export class GameDirector {
  private readonly spaceScene: MainScene;
  private readonly surfaceScene: PlanetSurfaceScene;
  private readonly stationScene: StationInteriorScene;
  private readonly cameraRig: CameraRig;
  private readonly input: InputManager;
  private readonly fade: FadeOverlay;
  private readonly prompt: PromptLayer;
  private readonly minimap: MinimapLayer;
  private readonly crosshair: CrosshairLayer;
  private readonly shipStatsHUD: ShipStatsHUD;
  private readonly astronautStatsHUD: AstronautStatsHUD;
  private readonly inventoryOverlay: InventoryOverlay;
  private readonly shopOverlay: ShopOverlay;
  private readonly missionOverlay: MissionOverlay;
  private readonly systemMapOverlay: SystemMapOverlay;
  private readonly waypointHUD: WaypointHUD;
  private readonly jumpHud: JumpHud;
  private readonly audio: AudioManager;
  private readonly fpsInventory: Inventory;
  private readonly shipUpgrades: ShipUpgrades;
  private readonly miningToolHUD: MiningToolHUD;
  private readonly underwaterOverlay: UnderwaterOverlay;
  private readonly rescueStatusHud: RescueStatusHud;
  private readonly pauseMenuOverlay: PauseMenuOverlay;
  private readonly missionManager: MissionManager;
  private readonly missionBus: MissionEventBus;
  private readonly portalContext: PortalContext | null;
  private portalRedirecting = false;
  private missionDestination: WaypointTarget | null = null;
  private missionDestinationBody: MapBody | null = null;
  private missionWaypointToken: MissionWaypointToken | null = null;
  private missionUnsub: (() => void) | null = null;
  // Surface-mode mission marker — used by tutorial steps that ask the
  // player to walk somewhere on the planet (the "Dust on Your Boots"
  // beacon, then back-to-cockpit). Only ever non-null while in
  // surface-astronaut mode with a matching mission token.
  private missionBeacon: MissionSurfaceBeacon | null = null;
  private readonly SURFACE_BEACON_REACH = 4.2;
  private readonly SURFACE_BEACON_OFFSET = 60;
  // Edge-triggered tracking for the long-range tutorial.
  private jumpAlignedDestId: string | null = null;
  private prevFuelLow = false;
  /** Edge tracker for "fuel just rose above the safe threshold" — used
   *  by the post-rescue refuel mission. Resets the moment fuel drops
   *  back below the threshold so a fresh refill re-fires. */
  private prevFuelFull = false;
  private static readonly REFUEL_TARGET_RATIO = 0.8;
  // ---- Combat / theft tutorial spawns -------------------------------
  /** Live tutorial pirate ship handle. Set when the director spawns it
   *  near Ambre, cleared when destroyed (via NPC observer) or when
   *  the mission moves past it. */
  private tutorialPirate: import('../npcs').NpcShip | null = null;
  /** Tutorial cargo target (a tagged miner ship the director picks
   *  when "Alternative Economics" needs a victim). Released when the
   *  ship is destroyed or the mission moves past it. */
  private tutorialCargoTarget: import('../npcs').NpcShip | null = null;
  /** Fixed virtual position of the "investigate the signal" point
   *  near Ambre. Cached the first time mission step asks for it so
   *  the position stays stable across frames. */
  private tutorialSignalPos: THREE.Vector3 | null = null;
  /** Edge tracker for "player reached the active mission target"
   *  proximity event — keyed by token so swapping tokens re-arms. */
  private missionTargetReachedToken: string | null = null;
  // Active step id snapshot, kept fresh by the mission subscription so
  // the per-frame tick can apply step-specific effects (e.g. the
  // autonomy-test scripted fuel drain) without coupling to mission
  // ids in the brain logic.
  private activeStepId: string | null = null;
  // Half-life of oxygen damage when depleted — keeps drown damage from
  // killing too quickly. Tuned so the player gets ~10s after O₂ hits 0
  // before they die outright.
  private oxygenDamageAccum = 0;

  private readonly rescue: RescueService;
  // Snapshot the previous rescue state every frame so we can fire one-shot
  // side-effects on transitions (prompt show/hide, transition start).
  private prevRescueState: RescueState = 'idle';
  private currentDestination: WaypointTarget | null = null;
  private currentDestinationBody: MapBody | null = null;
  private jumpController: JumpController | null = null;

  private fpsController: FirstPersonController | null = null;
  private surfaceShipController: SurfaceShipController | null = null;
  private stationFpsController: FirstPersonController | null = null;
  private fpsMiningTool: FpsMiningTool | null = null;

  private activeScene: Scene;
  private mode: GameModeId = 'space-flying';
  private transitioning = false;
  private spaceFollowController: FollowController | null = null;

  private readonly tmpBodyPos = new THREE.Vector3();
  private readonly tmpVec = new THREE.Vector3();
  private nearestLandable: Landable | null = null;
  private lastLandedBody: Landable | null = null;
  private nearStation = false;
  private currentShopCounter: StationCounter | null = null;

  constructor(deps: GameDirectorDependencies) {
    this.spaceScene = deps.spaceScene;
    this.surfaceScene = deps.surfaceScene;
    this.stationScene = deps.stationScene;
    this.cameraRig = deps.cameraRig;
    this.input = deps.input;
    this.fade = deps.fadeOverlay;
    this.prompt = deps.promptLayer;
    this.minimap = deps.minimapLayer;
    this.crosshair = deps.crosshairLayer;
    this.shipStatsHUD = deps.shipStatsHUD;
    this.astronautStatsHUD = deps.astronautStatsHUD;
    this.inventoryOverlay = deps.inventoryOverlay;
    this.shopOverlay = deps.shopOverlay;
    this.missionOverlay = deps.missionOverlay;
    this.systemMapOverlay = deps.systemMapOverlay;
    this.waypointHUD = deps.waypointHUD;
    this.jumpHud = deps.jumpHud;
    this.audio = deps.audio;
    this.fpsInventory = deps.fpsInventory;
    this.shipUpgrades = deps.shipUpgrades;
    this.miningToolHUD = deps.miningToolHUD;
    this.underwaterOverlay = deps.underwaterOverlay;
    this.rescueStatusHud = deps.rescueStatusHud;
    this.pauseMenuOverlay = deps.pauseMenuOverlay;
    this.missionManager = deps.missionManager;
    this.missionBus = deps.missionBus;
    this.portalContext = deps.portalContext ?? null;
    // The overlay is created in Application.ts before the director
    // exists; re-bind its Continue handler now so it routes through the
    // director's resume logic (which restores controllers + pointer).
    this.pauseMenuOverlay.setOnContinue(() => this.closePauseMenu());

    // Recovery tug — flies from the station to the player's ship when
    // they call for help on the radio. Lives in space and is parented
    // to the space scene root only while a rescue is active.
    this.rescue = new RescueService({
      getPlayerPosition: () =>
        this.spaceScene.getPlayerShip()?.object3D.position ?? null,
      getStationPosition: () => this.spaceScene.getStationPosition(),
      // Register through the EntityRegistry so the planet orbit-tug
      // system also catches the tug — without it, a player drifting
      // along with an orbiting planet leaves the tug permanently
      // behind. EntityRegistry.add already attaches `object3D` to the
      // scene root, so we don't double-add.
      attachToScene: (tug) => {
        this.spaceScene.entities.add(tug);
        return () => this.spaceScene.entities.remove(tug.id);
      }
    });
    this.activeScene = this.spaceScene;
    this.initJumpController();

    // Sync key handler — runs inside the keydown event so requestPointerLock
    // is invoked from a fresh user gesture (no extra click required).
    this.input.onKeyDown((code) => this.handleKeyDownSync(code));
  }

  private handleKeyDownSync(code: string): void {
    if (this.transitioning) return;

    // Pause menu — toggle on Tab. (ESC is hijacked by the browser to
    // release pointer lock, so the keydown either never fires or fires
    // out of order. Tab is the next-best ergonomic choice and Tab's
    // default focus-shift is suppressed in InputManager.)
    if (this.pauseMenuOverlay.isOpen()) {
      if (code === 'Tab' || code === 'Escape') {
        this.closePauseMenu();
        this.input.consumeKey(code);
      }
      return;
    }

    if (this.inventoryOverlay.isOpen()) {
      if (code === 'KeyI' || code === 'Escape') {
        this.closeInventory();
        this.input.consumeKey(code);
      }
      return;
    }
    if (this.shopOverlay.isOpen()) {
      if (code === 'KeyE' || code === 'Escape') {
        this.closeShop();
        this.input.consumeKey(code);
      }
      return;
    }
    if (this.missionOverlay.isOpen()) {
      if (code === 'KeyE' || code === 'Escape') {
        this.closeMissions();
        this.input.consumeKey(code);
      }
      return;
    }
    if (this.systemMapOverlay.isOpen()) {
      if (code === 'KeyM' || code === 'Escape') {
        this.closeSystemMap();
        this.input.consumeKey(code);
      }
      return;
    }

    // Nothing else open — Tab opens the pause menu.
    if (code === 'Tab') {
      this.openPauseMenu();
      this.input.consumeKey(code);
    }
  }

  async init(): Promise<void> {
    this.applyCameraForSpaceFlying();
    this.audio.startSpaceAmbient();
    this.audio.startEngine();

    // Mission marker — driven by MissionManager. Re-resolved every time
    // the active step changes so the marker always points at the
    // current objective (or clears when the active step has none).
    // Manual destination remains untouched: the two coexist as
    // independent slots in the waypoint HUD.
    this.missionUnsub = this.missionManager.subscribe((snap) => {
      this.activeStepId = snap.activeStepId;
      this.applyMissionWaypoint(snap);
    });
    // Tutorial mission needs to know when its tagged ships die.
    // Hooking through the scene's observer keeps the combat system
    // free of mission-aware code.
    this.spaceScene.setNpcDestroyedObserver((npc) =>
      this.handleNpcDestroyed(npc)
    );
  }

  getActiveScene(): Scene {
    return this.activeScene;
  }

  getMode(): GameModeId {
    return this.mode;
  }

  wantsPointerLock(): boolean {
    if (this.pauseMenuOverlay.isOpen()) return false;
    if (this.inventoryOverlay.isOpen()) return false;
    if (this.shopOverlay.isOpen()) return false;
    if (this.missionOverlay.isOpen()) return false;
    if (this.systemMapOverlay.isOpen()) return false;
    return (
      this.mode === 'surface-astronaut' ||
      this.mode === 'surface-ship' ||
      this.mode === 'space-flying' ||
      this.mode === 'station-interior'
    );
  }

  update(dt: number): void {
    // Pause menu freezes the world — skip rescue tick, mode update,
    // everything. The active scene is still rendered normally so the
    // player sees the dimmed game behind the panel.
    if (this.pauseMenuOverlay.isOpen()) return;

    // Rescue tug ticks every frame regardless of game mode so its
    // position is consistent across transitions. The state-transition
    // side-effects (prompt, fade-back-to-station) fire AFTER the
    // per-mode update below so the rescue prompt overrides any
    // landing/docking prompt the space-flying handler may have set.
    this.rescue.update(dt);

    if (this.transitioning) return;

    // Hyperspace jump fully owns the ship while charging or jumping —
    // don't let menu opens or prompts steal focus from the autopilot.
    // (J key handling for both starting and cancelling lives in JumpController.)
    if (this.jumpController?.isActive()) {
      this.jumpController.update(dt);
      return;
    }

    // Inventory toggle — open path. Close path is handled synchronously inside
    // handleKeyDownSync so requestPointerLock has a fresh user gesture.
    if (
      !this.inventoryOverlay.isOpen() &&
      !this.shopOverlay.isOpen() &&
      !this.missionOverlay.isOpen() &&
      !this.systemMapOverlay.isOpen() &&
      this.input.isKeyJustPressed('KeyI')
    ) {
      this.openInventory();
      return;
    }

    // Open the system map only when actually flying the ship in space.
    if (
      !this.systemMapOverlay.isOpen() &&
      !this.inventoryOverlay.isOpen() &&
      !this.shopOverlay.isOpen() &&
      !this.missionOverlay.isOpen() &&
      this.mode === 'space-flying' &&
      this.input.isKeyJustPressed('KeyM')
    ) {
      this.openSystemMap();
      return;
    }

    if (this.inventoryOverlay.isOpen()) return;
    if (this.shopOverlay.isOpen()) return;
    if (this.missionOverlay.isOpen()) return;
    if (this.systemMapOverlay.isOpen()) {
      this.waypointHUD.element.classList.remove('is-visible');
      return;
    }

    // Show the on-screen waypoint marker only when actually flying in
    // space. Either the manual destination OR the mission marker keep
    // the HUD alive — both render through their own slot.
    if (
      this.mode === 'space-flying' &&
      (this.currentDestination || this.missionDestination)
    ) {
      this.waypointHUD.update();
    } else {
      this.waypointHUD.element.classList.remove('is-visible');
    }

    if (this.shouldTriggerPlayerDestruction()) {
      void this.triggerPlayerDestruction();
      return;
    }

    // Underwater state only applies on foot on a planet — clear the
    // overlay any time we're elsewhere so a previous-frame "submerged"
    // tint doesn't leak into space or the station. Oxygen passively
    // refills in safe environments (ship cockpit, station, space) so
    // the player isn't penalised for a half-empty tank between dives.
    if (this.mode !== 'surface-astronaut') {
      this.underwaterOverlay.setUnderwater(false);
      this.oxygenDamageAccum = 0;
      const stats = this.spaceScene.getStats();
      if (stats.oxygen < stats.oxygenMax) stats.restoreOxygen(dt * 30);
    }

    switch (this.mode) {
      case 'space-flying':
        // The jump controller runs before the rest of space-flying logic so
        // the ship's autopilot transform is applied this frame; the landing
        // prompt etc. then sees the post-jump position.
        this.jumpController?.update(dt);
        this.updateSpaceFlying();
        this.updateSpaceMinimap();
        this.updateStartPortal(dt);
        this.updateLongRangeTutorial(dt);
        break;
      case 'surface-astronaut':
        this.fpsController?.update(dt);
        this.fpsMiningTool?.update(dt);
        this.updateSurfaceAstronaut();
        this.updateUnderwater(dt);
        this.updateMinimap();
        break;
      case 'surface-ship':
        this.surfaceShipController?.update(dt);
        this.updateSurfaceShip();
        break;
      case 'station-interior':
        this.stationFpsController?.update(dt);
        this.updateStationInterior();
        this.updateStationVibejamPortal();
        break;
      default:
        break;
    }

    // Rescue prompt runs after the mode-specific updates so it
    // overrides any landing/docking prompt the space-flying handler
    // may have just set when the tug is parked next to the player.
    this.handleRescueTransitions();
  }

  private toggleInventory(): void {
    if (this.inventoryOverlay.isOpen()) this.closeInventory();
    else this.openInventory();
  }

  /** Pause everything and surface the menu overlay. Disables every
   *  controller, releases pointer lock, hides the contextual prompt.
   *  The world keeps rendering (dimmed backdrop) but no simulation
   *  runs while the overlay is up — see `update()`. */
  private openPauseMenu(): void {
    if (this.pauseMenuOverlay.isOpen()) return;
    this.pauseMenuOverlay.show();
    this.spaceScene.getShipControl()?.setEnabled(false);
    this.fpsController?.setEnabled(false);
    this.surfaceShipController?.setEnabled(false);
    this.stationFpsController?.setEnabled(false);
    this.fpsMiningTool?.setEnabled(false);
    this.prompt.hide();
    if (document.exitPointerLock) document.exitPointerLock();
  }

  /** Resume play — restores the controller for the active mode and
   *  asks for pointer lock so the camera + cursor reattach to the
   *  game without an extra click. */
  private closePauseMenu(): void {
    this.pauseMenuOverlay.hide();
    if (this.mode === 'space-flying') {
      this.spaceScene.getShipControl()?.setEnabled(true);
    } else if (this.mode === 'surface-ship') {
      this.surfaceShipController?.setEnabled(true);
    } else if (this.mode === 'surface-astronaut') {
      this.fpsController?.setEnabled(true);
      this.fpsMiningTool?.setEnabled(true);
    } else if (this.mode === 'station-interior') {
      this.stationFpsController?.setEnabled(true);
    }
    this.input.requestPointerLock();
  }

  private openInventory(): void {
    // Don't open the inventory on top of a shop/mission overlay.
    if (this.shopOverlay.isOpen() || this.missionOverlay.isOpen()) return;
    const tabMode = this.resolveInventoryTabs();
    this.inventoryOverlay.show(tabMode, {
      onRequestRescue: () => this.handleRescueRequest(),
      getRadioState: () => this.buildRadioState(),
      onTabActivated: (tab) => {
        if (tab === 'radio') this.missionBus.emit('inventoryRadioOpened', {});
      }
    });
    this.spaceScene.getShipControl()?.setEnabled(false);
    this.fpsController?.setEnabled(false);
    this.surfaceShipController?.setEnabled(false);
    this.stationFpsController?.setEnabled(false);
    this.fpsMiningTool?.setEnabled(false);
    this.prompt.hide();
    if (document.exitPointerLock) document.exitPointerLock();
  }

  private closeInventory(): void {
    this.inventoryOverlay.hide();
    if (this.mode === 'space-flying') {
      this.spaceScene.getShipControl()?.setEnabled(true);
    } else if (this.mode === 'surface-ship') {
      this.surfaceShipController?.setEnabled(true);
    } else if (this.mode === 'surface-astronaut') {
      this.fpsController?.setEnabled(true);
      this.fpsMiningTool?.setEnabled(true);
    } else if (this.mode === 'station-interior') {
      this.stationFpsController?.setEnabled(true);
    }
    this.input.requestPointerLock();
  }

  /** Build the radio panel snapshot for the inventory's "Radio" tab. The
   *  rescue button is enabled only when fuel is exactly 0 and no rescue
   *  is already in flight — players can't burn the call for a
   *  not-quite-empty tank. */
  private buildRadioState(): {
    canRequestRescue: boolean;
    rescueActive: boolean;
    rescueDescription: string;
  } {
    const stats = this.spaceScene.getStats();
    const rescueState = this.rescue.getState();
    const rescueActive = rescueState !== 'idle';
    const fuelEmpty = stats.fuel <= 0;
    return {
      canRequestRescue: !rescueActive && fuelEmpty,
      rescueActive,
      rescueDescription: rescueActive
        ? rescueState === 'arrived'
          ? 'Tug parked alongside. Hail the pilot from the cockpit window to dock.'
          : 'Tug en route from the station. Watch the minimap.'
        : fuelEmpty
          ? 'Fuel reserves dry. Sector Recovery can tow you home with a 20% top-up — no charge.'
          : 'Available only when your fuel reads zero. Recovery will not tow a fuelled ship.'
    };
  }

  private handleRescueRequest(): void {
    if (this.rescue.getState() !== 'idle') return;
    if (this.spaceScene.getStats().fuel > 0) return;
    this.rescue.request();
    this.missionBus.emit('rescueRequested', {});
    this.audio.playConfirm();
    // Close the inventory so the player can see the tug en route.
    if (this.inventoryOverlay.isOpen()) this.closeInventory();
  }

  /** Drive UI side-effects on rescue state changes — show/hide the
   *  cockpit accept prompt, kick off the fade-back-to-station once the
   *  player accepts. Runs after the per-mode update so the rescue
   *  prompt overrides any landing/dock prompt that may have fired. */
  private handleRescueTransitions(): void {
    const state = this.rescue.getState();

    // Edge-triggered emit when the tug enters the 'arrived' state for
    // the first time this rescue. Mission tutorial advances on this.
    if (state === 'arrived' && this.prevRescueState !== 'arrived') {
      this.missionBus.emit('rescueTugArrived', {});
    }

    // En-route status HUD — persistent banner with ETA so the player
    // doesn't wonder "did the call go through?" while waiting.
    if (state === 'en-route' && this.mode === 'space-flying') {
      const eta = this.rescue.getEstimatedSeconds();
      if (eta !== null) this.rescueStatusHud.setStatus(eta);
    } else if (this.rescueStatusHud.isVisible()) {
      this.rescueStatusHud.hide();
    }

    if (state === 'arrived' && this.mode === 'space-flying') {
      if (
        !this.inventoryOverlay.isOpen() &&
        !this.shopOverlay.isOpen() &&
        !this.missionOverlay.isOpen() &&
        !this.systemMapOverlay.isOpen() &&
        !this.jumpController?.isActive()
      ) {
        const wasVisible = this.prompt.isVisible();
        // Override any landing/docking prompt that updateSpaceFlying
        // may have just shown — the parked tug is the priority cue.
        this.prompt.show('E', 'Accept rescue tow');
        if (!wasVisible && this.prevRescueState !== 'arrived') {
          this.audio.playPrompt();
        }
        if (this.input.isKeyJustPressed('KeyE')) {
          // Consume so updateSpaceFlying's landing/docking handler that
          // already saw this frame's E press doesn't double-fire.
          this.input.consumeKey('KeyE');
          void this.triggerRescueAccept();
        }
      }
    }

    this.prevRescueState = state;
  }

  /** Tow the player back to the station with a small fuel top-up.
   *  Reuses the existing fade-then-teleport pattern used by other
   *  cinematic transitions. */
  private async triggerRescueAccept(): Promise<void> {
    if (this.transitioning) return;
    if (this.rescue.getState() !== 'arrived') return;
    this.transitioning = true;
    this.rescue.accept();
    this.jumpController?.forceCancel();
    this.jumpHud.hide();
    this.prompt.hide();
    this.audio.playConfirm();
    this.audio.playWhoosh();
    this.spaceScene.getShipControl()?.setEnabled(false);

    await withFade(this.fade, 540, 200, async () => {
      this.spaceScene.placePlayerAtStationExit();
      // 20% fuel top-up so the player can dock and refuel without
      // needing extra credits. Cap at fuelMax just in case the player
      // somehow has a tank above zero by the time the fade fires.
      const stats = this.spaceScene.getStats();
      const target = stats.fuelMax * 0.2;
      const missing = Math.max(0, target - stats.fuel);
      if (missing > 0) stats.addFuel(missing);

      const control = this.spaceScene.getShipControl();
      control?.syncFromShip();
      control?.setEnabled(true);

      this.rescue.complete();
      this.prevRescueState = 'idle';
    });

    this.missionBus.emit('rescueTowAccepted', {});

    this.transitioning = false;
  }

  private resolveInventoryTabs(): 'both' | 'fps-only' {
    if (
      this.mode === 'space-flying' ||
      this.mode === 'surface-ship' ||
      this.mode === 'station-interior'
    ) {
      return 'both';
    }
    // surface-astronaut: check distance to ship.
    try {
      const astronaut = this.surfaceScene.astronautEntity.object3D.position;
      const ship = this.surfaceScene.shipEntity.object3D.position;
      const dx = astronaut.x - ship.x;
      const dz = astronaut.z - ship.z;
      return dx * dx + dz * dz < 100 ? 'both' : 'fps-only';
    } catch {
      return 'fps-only';
    }
  }

  private shouldTriggerPlayerDestruction(): boolean {
    if (this.mode !== 'space-flying' && this.mode !== 'surface-ship') {
      return false;
    }
    return this.spaceScene.getStats().hull <= 0;
  }

  private updateMinimap(): void {
    if (!this.fpsController) return;
    const astronaut = this.surfaceScene.astronautEntity.object3D.position;
    const ship = this.surfaceScene.shipEntity.object3D.position;
    const miniBase = this.surfaceScene.getMiniBase();

    // Permanent landmark — the Caelis mini base shows up on the radar
    // every visit, even after its tutorial step is done. Cyan dot so
    // it reads distinct from blips, ore, the ship anchor, and the
    // mission diamond.
    const blips: { position: THREE.Vector3; color: string }[] = [];
    if (miniBase) {
      blips.push({
        position: miniBase.position,
        color: 'rgba(180, 227, 255, 0.95)'
      });
    }

    // Surface mission marker: red diamond at the beacon, the cockpit,
    // or the mini base depending on the active step. The ship anchor
    // stays orange — it's the ambient "where I parked" cue, not a
    // mission cue.
    let missionTarget:
      | null
      | { position: THREE.Vector3; color: string; label?: string } = null;
    const token = this.missionWaypointToken;
    if (token === 'surface-beacon' && this.missionBeacon) {
      missionTarget = {
        position: this.missionBeacon.position,
        color: 'rgba(255, 93, 93, 1)',
        label: 'Surface Signal'
      };
    } else if (token === 'surface-ship') {
      missionTarget = {
        position: ship,
        color: 'rgba(255, 93, 93, 1)',
        label: 'Cockpit'
      };
    } else if (token === 'caelis-base' && miniBase) {
      missionTarget = {
        position: miniBase.position,
        color: 'rgba(255, 93, 93, 1)',
        label: 'Mini Base'
      };
    }

    this.minimap.update(astronaut.x, astronaut.z, this.fpsController.getYaw(), {
      target: {
        position: ship,
        color: 'rgba(255, 140, 66, 1)',
        label: 'Ship'
      },
      blips,
      missionTarget
    });
  }

  private updateSpaceMinimap(): void {
    const ship = this.spaceScene.getPlayerShip();
    const director = this.spaceScene.getNpcDirector();
    if (!ship || !director) return;

    const blips: MinimapBlip[] = [];
    director.forEachRadarTarget((position, hostile) => {
      blips.push({
        position,
        color: hostile ? 'rgba(255, 90, 90, 1)' : 'rgba(110, 200, 255, 1)'
      });
    });

    // Recovery tug — show a distinct amber blip while it's en route or
    // parked next to the player.
    const tugPos = this.rescue.getTugPosition();
    if (tugPos) {
      blips.push({
        position: tugPos,
        color: 'rgba(255, 200, 80, 1)'
      });
    }

    const celestials: MinimapCelestial[] = [];
    for (const body of this.spaceScene.getMapBodies()) {
      body.getWorldPosition(this.tmpVec);
      celestials.push({
        position: this.tmpVec.clone(),
        kind: body.kind as MinimapCelestialKind,
        color: hexToCss(body.color)
      });
    }

    const yaw = ship.object3D.rotation.y;
    const pos = ship.object3D.position;

    // Mission marker on the disc — distinct from the manual destination.
    // Saturated red so it reads as the priority cue even with hostile
    // blips on screen. Only shown when the active step targets a known
    // body.
    let missionTarget = null as
      | null
      | { position: THREE.Vector3; color: string; label?: string };
    if (this.missionDestinationBody) {
      const missionPos = new THREE.Vector3();
      this.missionDestinationBody.getWorldPosition(missionPos);
      missionTarget = {
        position: missionPos,
        color: 'rgba(255, 93, 93, 1)',
        label: this.missionDestinationBody.name
      };
    } else if (this.missionWaypointToken === 'rescue-tug') {
      // Mobile target — read the live tug position so the diamond
      // tracks it. The amber blip from the rescue service is still
      // drawn underneath; the red diamond elevates the tug to mission
      // priority while the relevant step is active.
      const tugPos = this.rescue.getTugPosition();
      if (tugPos) {
        missionTarget = {
          position: tugPos,
          color: 'rgba(255, 93, 93, 1)',
          label: 'Recovery Tug'
        };
      }
    }

    this.minimap.update(pos.x, pos.z, yaw, {
      radius: SPACE_MINIMAP_RADIUS,
      blips,
      celestials,
      missionTarget
    });
  }

  private updateSpaceFlying(): void {
    const ship = this.spaceScene.getPlayerShip();
    const landables = this.spaceScene.getLandables();
    if (!ship || landables.length === 0) {
      // Don't tear down the prompt if the rescue handler is going to
      // own it this frame — otherwise the prompt ping-pongs once per
      // tick (hide here, show in handleRescueTransitions).
      if (!this.rescue.isArrived()) this.prompt.hide();
      this.nearestLandable = null;
      return;
    }

    // While the tug is parked, the rescue handler exclusively owns the
    // prompt — skip the landing/dock checks so they don't fight it.
    if (this.rescue.isArrived()) {
      this.nearestLandable = null;
      this.nearStation = false;
      return;
    }

    const star = this.spaceScene.getStar();
    if (star) {
      star.getWorldPosition(this.tmpBodyPos);
      const dist = ship.object3D.position.distanceTo(this.tmpBodyPos);
      if (dist <= star.radius) {
        this.prompt.hide();
        void this.triggerStarDeath();
        return;
      }
    }

    let nearest: Landable | null = null;
    let nearestSurfaceDist = Infinity;
    for (const body of landables) {
      body.getWorldPosition(this.tmpBodyPos);
      const dist = ship.object3D.position.distanceTo(this.tmpBodyPos);
      const surfaceDist = dist - body.radius;
      if (surfaceDist < nearestSurfaceDist) {
        nearestSurfaceDist = surfaceDist;
        nearest = body;
      }
    }
    this.nearestLandable = nearest;

    // Station proximity check (takes priority over landing prompt if closer).
    const station = this.spaceScene.getStation();
    let stationInRange = false;
    let stationDockDist = Infinity;
    if (station) {
      const stationCenter = this.spaceScene.getStationPosition();
      const dist = ship.object3D.position.distanceTo(stationCenter);
      stationDockDist = dist - station.interactionRadius;
      if (stationDockDist <= STATION_APPROACH_EXTRA) stationInRange = true;
    }

    if (stationInRange) {
      const wasVisible = this.prompt.isVisible();
      this.prompt.show('E', 'Dock at station');
      if (!wasVisible) this.audio.playPrompt();
      this.nearStation = true;
      if (this.input.isKeyJustPressed('KeyE')) {
        void this.triggerStationEntry();
      }
      return;
    }
    this.nearStation = false;

    if (!nearest) {
      if (!this.rescue.isArrived()) this.prompt.hide();
      return;
    }

    // No landing on an empty tank — the rescue flow is the only
    // legal exit. Skipping the prompt entirely also keeps it from
    // racing the rescue accept prompt (which fires from
    // `handleRescueTransitions` once the tug parks).
    const fuelEmpty = this.spaceScene.getStats().fuel <= 0;
    if (fuelEmpty) {
      if (!this.rescue.isArrived()) this.prompt.hide();
      return;
    }

    if (nearestSurfaceDist <= 0) {
      this.prompt.hide();
      void this.triggerLanding(nearest);
      return;
    }

    if (nearestSurfaceDist < LANDING_DISTANCE_MARGIN) {
      const wasVisible = this.prompt.isVisible();
      this.prompt.show('E', `Land on ${nearest.displayName}`);
      if (!wasVisible) this.audio.playPrompt();
      if (this.input.isKeyJustPressed('KeyE')) {
        void this.triggerLanding(nearest);
      }
    } else if (!this.rescue.isArrived()) {
      this.prompt.hide();
    }
  }

  private updateStationInterior(): void {
    const astronaut = this.stationScene.astronautEntity;
    const pos = astronaut.object3D.position;

    // Check proximity to exit pad first.
    const exit = this.stationScene.getExitPosition();
    const dx = pos.x - exit.x;
    const dz = pos.z - exit.z;
    const exitDist = Math.hypot(dx, dz);
    if (exitDist < this.stationScene.getExitRadius() + STATION_EXIT_REACH) {
      this.prompt.show('E', 'Exit station');
      if (this.input.isKeyJustPressed('KeyE')) {
        void this.triggerStationExit();
      }
      this.currentShopCounter = null;
      return;
    }

    // Check proximity to the central mast (mission terminal).
    const centerDist = Math.hypot(pos.x, pos.z);
    if (centerDist < MISSION_INTERACT_RANGE) {
      this.prompt.show('E', 'Open mission terminal');
      if (this.input.isKeyJustPressed('KeyE')) {
        this.openMissions();
      }
      this.currentShopCounter = null;
      return;
    }

    // Check proximity to shop counters.
    let closestCounter: StationCounter | null = null;
    let closestDist = Infinity;
    for (const counter of this.stationScene.getCounters()) {
      counter.getWorldInteractionPoint(this.tmpVec);
      const ddx = pos.x - this.tmpVec.x;
      const ddz = pos.z - this.tmpVec.z;
      const dist = Math.hypot(ddx, ddz);
      if (dist < closestDist && dist < SHOP_INTERACT_RANGE) {
        closestDist = dist;
        closestCounter = counter;
      }
    }
    this.currentShopCounter = closestCounter;

    if (closestCounter) {
      const def = closestCounter.definition;
      this.prompt.show('E', def.promptLabel);
      if (this.input.isKeyJustPressed('KeyE')) {
        this.openShop(closestCounter);
      }
    } else {
      this.prompt.hide();
    }
  }

  /** Drive the underwater tint + oxygen drain. Sampled once per frame
   *  while the player is on foot on a planet surface; off-planet modes
   *  always read as "not underwater". */
  private updateUnderwater(dt: number): void {
    const astronaut = this.surfaceScene.astronautEntity;
    const stats = this.surfaceScene.getStats();
    // Eye height matches DEFAULT_FPS_CONFIG.eyeHeight; submerging happens
    // when the head is below the lake surface.
    const eyeY = astronaut.object3D.position.y + 1.62;
    const submerged = this.surfaceScene.isHeadUnderwater(
      astronaut.object3D.position.x,
      astronaut.object3D.position.z,
      eyeY
    );
    this.underwaterOverlay.setUnderwater(submerged);

    if (submerged) {
      // Drain ~6 oxygen / second → 100 max gives ~16s of breath. After
      // that, ramp up health damage smoothly via an accumulator so we
      // don't shotgun damage in a single tick.
      stats.consumeOxygen(dt * 6);
      if (stats.oxygen <= 0) {
        this.oxygenDamageAccum += dt;
        const interval = 0.5;
        while (this.oxygenDamageAccum >= interval) {
          this.oxygenDamageAccum -= interval;
          stats.damageHealth(8);
        }
      } else {
        this.oxygenDamageAccum = 0;
      }
    } else {
      // Above-water: refill steadily, faster than drain so a brief dip
      // recovers within a couple of seconds.
      stats.restoreOxygen(dt * 22);
      this.oxygenDamageAccum = 0;
    }
  }

  private updateSurfaceAstronaut(): void {
    const astronaut = this.surfaceScene.astronautEntity;
    const ship = this.surfaceScene.shipEntity;
    const dx = astronaut.object3D.position.x - ship.object3D.position.x;
    const dz = astronaut.object3D.position.z - ship.object3D.position.z;
    const distanceSq = dx * dx + dz * dz;

    // Mini base interaction takes priority over the cockpit prompt
    // while the active mission step is asking the player to activate
    // the terminal — otherwise the cockpit prompt clobbers it the
    // moment the player walks past the parked ship on the way back.
    if (this.handleMiniBaseInteraction(astronaut.object3D.position)) {
      this.updateSurfaceMissionBeacon(astronaut.object3D.position);
      return;
    }

    if (distanceSq < ENTER_SHIP_RANGE * ENTER_SHIP_RANGE) {
      this.prompt.show('E', 'Enter ship');
      if (this.input.isKeyJustPressed('KeyE')) {
        this.enterShip();
      }
    } else {
      this.prompt.hide();
    }

    this.updateSurfaceMissionBeacon(astronaut.object3D.position);
  }

  /** Tick the mini base animation + run the proximity-prompt logic.
   *  Returns true when the prompt was shown for the terminal so the
   *  caller can skip its own competing prompt this frame. */
  private handleMiniBaseInteraction(astronautPos: THREE.Vector3): boolean {
    const base = this.surfaceScene.getMiniBase();
    if (!base) return false;
    base.update(1 / 60);
    // The terminal prompt only lights up when the active mission step
    // is the "activate the terminal" beat. Once the step completes
    // (or the player isn't in that mission), the mini base just sits
    // there as scenery + minimap landmark.
    if (!this.isMiniBaseStepActive()) return false;
    const ip = base.interactionPoint;
    const dx = astronautPos.x - ip.x;
    const dz = astronautPos.z - ip.z;
    const distSq = dx * dx + dz * dz;
    const reach = 3.6;
    if (distSq > reach * reach) return false;
    this.prompt.show('E', 'Activate terminal');
    if (this.input.isKeyJustPressed('KeyE')) {
      this.input.consumeKey('KeyE');
      this.missionBus.emit('surfaceTerminalActivated', {
        terminalId: 'caelis-mini-base'
      });
    }
    return true;
  }

  private isMiniBaseStepActive(): boolean {
    const snap = this.missionManager.snapshot();
    // The buy/use steps don't carry the surfaceTerminalActivated event
    // — only the "activate the mini base terminal" step does, so this
    // is enough to gate the prompt without coupling to a specific
    // mission id.
    return this.missionWaypointToken === 'caelis-base';
  }

  /** Drive the surface mission beacon's flat-distance check + per-frame
   *  pulse animation. The beacon is one-shot: once the player walks
   *  inside `SURFACE_BEACON_REACH`, fire the event and dispose so the
   *  beacon doesn't keep pulsing while the next step is "return to
   *  cockpit". */
  private updateSurfaceMissionBeacon(astronautPos: THREE.Vector3): void {
    const beacon = this.missionBeacon;
    if (!beacon) return;
    // The beacon mesh isn't owned by a system, so we tick it from the
    // director's own surface update.
    beacon.update(1 / 60);
    const bx = beacon.position.x - astronautPos.x;
    const bz = beacon.position.z - astronautPos.z;
    const distSq = bx * bx + bz * bz;
    if (distSq <= this.SURFACE_BEACON_REACH * this.SURFACE_BEACON_REACH) {
      this.missionBus.emit('surfaceMarkerReached', { markerId: 'tutorial' });
      this.disposeMissionBeacon();
    }
  }

  private updateSurfaceShip(): void {
    if (!this.surfaceShipController) return;

    if (this.input.isKeyJustPressed('KeyQ')) {
      void this.triggerTakeoff();
      return;
    }

    const controller = this.surfaceShipController;
    const inFlight = controller.isInFlight();
    const altitudeAbove = controller.getAltitudeAboveGround();

    if (!inFlight) {
      this.prompt.show([
        { key: 'Space', label: 'Takeoff' },
        { key: 'Q', label: 'Leave planet' },
        { key: 'E', label: 'Exit ship' }
      ]);
      if (this.input.isKeyJustPressed('KeyE')) {
        this.exitShip();
      }
    } else if (altitudeAbove < 8) {
      this.prompt.show([
        { key: 'Q', label: 'Leave planet' },
        { key: 'E', label: 'Exit ship' }
      ]);
      if (this.input.isKeyJustPressed('KeyE')) {
        this.exitShip();
      }
    } else {
      this.prompt.show({ key: 'Q', label: 'Leave planet' });
    }
  }

  /** Restore the player onto a planet surface from a save file. Runs
   *  the same prepare-and-bind logic as a normal landing but skips
   *  the "place ship at default pad / astronaut next to ship" step,
   *  using the saved transforms instead. Called by Application after
   *  the save service has applied its synchronous payload.
   *
   *  `mode` decides whether the player wakes up on foot (`surface-
   *  astronaut`) or in the cockpit on the ground (`surface-ship`).
   *  Anything else falls back to space-flying. */
  async restoreToSurface(
    payload: SurfaceSavePayload,
    mode: GameModeId
  ): Promise<boolean> {
    const target = this.spaceScene
      .getLandables()
      .find((b) => b.displayName === payload.planetName);
    if (!target) return false;

    this.transitioning = true;
    this.spaceScene.getShipControl()?.setEnabled(false);
    // Hide the canvas while we swap scenes — the loop hasn't started
    // yet (load runs at boot), but `prepareFor` does GPU work that may
    // briefly draw the wrong frame. The fade clears just before
    // loop.start() takes over.
    this.fade.setAlpha(1);

    const sky = this.buildSkyConfig(target);
    await this.surfaceScene.prepareFor({
      palette: target.palette,
      name: target.displayName,
      sky
    });
    this.bindSurfaceControllers();

    // Restore the surface ship pose (where the player parked it).
    // The terrain is regenerated with a fresh procedural seed on every
    // prepareFor, so the saved Y no longer matches the new ground.
    // Re-sample the terrain at the saved X/Z and re-clamp the ship
    // 1.4u above ground (same constant `resetShipToLanded` uses) so
    // it sits naturally on the surface instead of floating.
    const ship = this.surfaceScene.shipEntity;
    const shipX = payload.ship.position[0] ?? 0;
    const shipZ = payload.ship.position[2] ?? 0;
    const shipGroundY = this.surfaceScene.terrainInstance.getCollisionGroundY(
      shipX,
      shipZ
    );
    ship.object3D.position.set(shipX, shipGroundY + 1.4, shipZ);
    ship.object3D.rotation.set(
      payload.ship.rotation[0] ?? 0,
      payload.ship.rotation[1] ?? 0,
      payload.ship.rotation[2] ?? 0
    );
    ship.velocity.set(0, 0, 0);

    // Restore the astronaut pose. Same terrain-reground trick — the
    // astronaut walks at exactly ground level on this scene, so the
    // saved Y is replaced with the current terrain sample.
    const astronaut = this.surfaceScene.astronautEntity;
    const astroX = payload.astronaut.position[0] ?? 0;
    const astroZ = payload.astronaut.position[2] ?? 0;
    const astroGroundY = this.surfaceScene.terrainInstance.getCollisionGroundY(
      astroX,
      astroZ
    );
    astronaut.object3D.position.set(astroX, astroGroundY, astroZ);
    astronaut.object3D.rotation.set(
      payload.astronaut.rotation[0] ?? 0,
      payload.astronaut.rotation[1] ?? 0,
      payload.astronaut.rotation[2] ?? 0
    );
    astronaut.velocity.set(0, 0, 0);
    astronaut.isGrounded = true;
    astronaut.object3D.visible = false;

    this.activeScene = this.surfaceScene;
    this.lastLandedBody = target;

    if (mode === 'surface-ship') {
      this.fpsController?.setEnabled(false);
      this.fpsMiningTool?.forceStow();
      this.fpsMiningTool?.setEnabled(false);
      this.surfaceShipController?.setEnabled(true);
      this.surfaceScene.getCombatSystem()?.setEnabled(true);
      this.mode = 'surface-ship';
      this.applyCameraForSurfaceShip();
    } else {
      // surface-astronaut (default for any non-ship surface mode)
      this.surfaceShipController?.setEnabled(false);
      // FPS yaw is the astronaut's saved Y rotation; pitch stays neutral.
      this.fpsController?.setYaw(payload.astronaut.rotation[1] ?? 0);
      this.fpsController?.setPitch(0);
      this.fpsController?.setEnabled(true);
      this.fpsMiningTool?.setEnabled(true);
      this.mode = 'surface-astronaut';
      this.applyCameraForAstronaut();
    }

    this.audio.stopSpaceAmbient();
    this.audio.stopEngine();
    this.audio.startPlanetAmbient();

    // Fade back in on the next frame so the player sees the
    // restored planet smoothly rather than a hard pop from black.
    void this.fade.fadeTo(0, 360);

    // Restore-to-surface skipped the landing fade path, so the beacon
    // spawn that triggerLanding does has to happen here too. The
    // mission state subscription already fired but hadn't found a
    // surface scene at that moment; this is the catch-up call.
    this.refreshSurfaceMissionState();

    this.transitioning = false;
    return true;
  }

  private async triggerLanding(target: Landable): Promise<void> {
    if (this.transitioning) return;
    this.transitioning = true;
    this.jumpController?.forceCancel();
    this.jumpHud.hide();
    this.prompt.hide();
    this.audio.playConfirm();
    this.audio.playWhoosh();

    this.spaceScene.getShipControl()?.setEnabled(false);

    await withFade(this.fade, 380, 120, async () => {
      const sky = this.buildSkyConfig(target);
      await this.surfaceScene.prepareFor({
        palette: target.palette,
        name: target.displayName,
        sky
      });
      this.bindSurfaceControllers();
      this.surfaceScene.resetShipToLanded();
      this.surfaceScene.placeAstronautNearShip();
      this.surfaceScene.astronautEntity.object3D.visible = false;
      this.activeScene = this.surfaceScene;
      this.mode = 'surface-astronaut';
      this.lastLandedBody = target;
      this.alignFpsYawToShip();
      this.fpsController?.setEnabled(true);
      this.surfaceShipController?.setEnabled(false);
      this.applyCameraForAstronaut();
      this.audio.stopSpaceAmbient();
      this.audio.stopEngine();
      this.audio.startPlanetAmbient();
    });

    this.missionBus.emit('planetLanded', { planetName: target.displayName });
    // The mission step may want a surface beacon as soon as we touch
    // down — e.g. the "reach the surface signal" step that activates
    // the moment we land on Caelis. Re-resolve so the spawn happens
    // before the player sees the new scene.
    this.refreshSurfaceMissionState();

    this.transitioning = false;
  }

  private async triggerStarDeath(): Promise<void> {
    await this.triggerPlayerDestruction();
  }

  private async triggerPlayerDestruction(): Promise<void> {
    if (this.transitioning) return;
    this.transitioning = true;
    this.jumpController?.forceCancel();
    this.jumpHud.hide();
    this.prompt.hide();

    const ship = this.spaceScene.getPlayerShip();
    const combat = this.spaceScene.getCombatSystem();
    if (ship && combat) {
      combat.triggerShipDestruction(
        ship.object3D.position,
        ship.collisionRadius * 2.2,
        ship.skin
      );
      this.audio.playExplosion(ship.collisionRadius * 2.2);
    }
    this.spaceScene.getShipControl()?.setEnabled(false);
    this.spaceScene.getHostileProjectiles()?.setEnabled(false);
    if (ship) ship.object3D.visible = false;

    // Cancel any active rescue — the tug stays in space but the player
    // is about to respawn at the station, so the call is moot.
    this.rescue.cancel();
    this.prevRescueState = 'idle';
    this.rescueStatusHud.hide();

    await withFade(this.fade, 820, 260, async () => {
      this.spaceScene.placePlayerAtSpawn();
      this.spaceScene.getStats().resetAll();
      this.spaceScene.getNpcDirector()?.resetHostility();
      if (ship) ship.object3D.visible = true;
      const control = this.spaceScene.getShipControl();
      control?.syncFromShip();
      control?.setEnabled(true);
      this.spaceScene.getHostileProjectiles()?.setEnabled(true);
      if (this.mode !== 'space-flying') {
        this.activeScene = this.spaceScene;
        this.mode = 'space-flying';
        this.applyCameraForSpaceFlying();
      }
    });

    this.transitioning = false;
  }

  private async triggerStationEntry(): Promise<void> {
    if (this.transitioning) return;
    this.transitioning = true;
    this.jumpController?.forceCancel();
    this.jumpHud.hide();
    this.prompt.hide();
    this.audio.playConfirm();
    this.audio.playWhoosh();
    this.spaceScene.getShipControl()?.setEnabled(false);

    await withFade(this.fade, 560, 180, async () => {
      await this.stationScene.load();
      this.bindStationController();
      this.stationScene.placeAstronautAtSpawn();
      // Hide the astronaut mesh — the camera sits inside the head in FPS mode.
      this.stationScene.astronautEntity.object3D.visible = false;
      this.activeScene = this.stationScene;
      this.mode = 'station-interior';
      this.alignStationFpsYawToSpawn();
      this.stationFpsController?.setEnabled(true);
      this.applyCameraForStation();
      this.audio.stopSpaceAmbient();
      this.audio.stopEngine();
      this.audio.startPlanetAmbient();
    });

    this.missionBus.emit('stationEntered', { stationId: 'station' });

    this.transitioning = false;
  }

  private async triggerStationExit(): Promise<void> {
    if (this.transitioning) return;
    this.transitioning = true;
    this.jumpController?.forceCancel();
    this.jumpHud.hide();
    this.prompt.hide();
    this.audio.playConfirm();
    this.audio.playWhoosh();
    this.stationFpsController?.setEnabled(false);

    await withFade(this.fade, 520, 180, async () => {
      this.spaceScene.placePlayerAtStationExit();
      const control = this.spaceScene.getShipControl();
      control?.syncFromShip();
      control?.setEnabled(true);
      this.activeScene = this.spaceScene;
      this.mode = 'space-flying';
      this.applyCameraForSpaceFlying();
      this.audio.stopPlanetAmbient();
      this.audio.startSpaceAmbient();
      this.audio.startEngine();
    });

    this.missionBus.emit('stationExited', { stationId: 'station' });

    this.transitioning = false;
  }

  private async triggerTakeoff(): Promise<void> {
    if (this.transitioning) return;
    this.transitioning = true;
    this.jumpController?.forceCancel();
    this.jumpHud.hide();
    this.prompt.hide();
    this.audio.playConfirm();
    this.audio.playWhoosh();
    this.surfaceShipController?.setEnabled(false);
    // Tear down the planet-bound mining tool — its scene root and explosions
    // are about to be wiped by the next prepareFor.
    this.disposeSurfaceMiningTool();
    // Beacon meshes live in the surface scene root; the impending
    // prepareFor would dispose them anyway, but we drop the local
    // reference so a stale beacon doesn't leak into the next landing.
    this.disposeMissionBeacon();

    await withFade(this.fade, 460, 140, async () => {
      if (this.lastLandedBody) {
        this.spaceScene.placePlayerNear(this.lastLandedBody);
      }
      const control = this.spaceScene.getShipControl();
      control?.syncFromShip();
      control?.setEnabled(true);
      this.activeScene = this.spaceScene;
      this.mode = 'space-flying';
      this.applyCameraForSpaceFlying();
      this.audio.stopPlanetAmbient();
      this.audio.startSpaceAmbient();
      this.audio.startEngine();
    });

    this.missionBus.emit('returnedToSpace', {});

    this.transitioning = false;
  }

  private enterShip(): void {
    this.prompt.hide();
    this.audio.playConfirm();
    this.mode = 'surface-ship';
    this.fpsController?.setEnabled(false);
    this.fpsMiningTool?.forceStow();
    this.fpsMiningTool?.setEnabled(false);
    this.surfaceScene.astronautEntity.object3D.visible = false;
    this.surfaceShipController?.setEnabled(true);
    this.surfaceScene.getCombatSystem()?.setEnabled(true);
    this.applyCameraForSurfaceShip();
    this.audio.startEngine();
    this.missionBus.emit('surfaceShipEntered', {});
    // Dropping back into surface-ship clears the on-foot beacon path:
    // re-resolve so the cockpit-target marker (or whatever the next
    // step wants) refreshes immediately.
    this.refreshSurfaceMissionState();
  }

  private exitShip(): void {
    this.prompt.hide();
    this.audio.playShipTouchdown();
    this.surfaceShipController?.setEnabled(false);
    this.surfaceScene.getCombatSystem()?.setEnabled(false);
    this.surfaceScene.landShipAtCurrentPosition();
    this.surfaceScene.placeAstronautNearShip();
    this.surfaceScene.astronautEntity.object3D.visible = false;
    this.mode = 'surface-astronaut';
    this.alignFpsYawToShip();
    this.fpsController?.setEnabled(true);
    this.fpsMiningTool?.setEnabled(true);
    this.applyCameraForAstronaut();
    // Player is back on foot — re-evaluate the beacon now in case the
    // active mission step wants one and we cleared it on enterShip.
    this.refreshSurfaceMissionState();
    this.audio.stopEngine();
  }

  private alignFpsYawToShip(): void {
    if (!this.fpsController) return;
    const shipRotY = this.surfaceScene.shipEntity.object3D.rotation.y;
    this.fpsController.setYaw(shipRotY + Math.PI);
    this.fpsController.setPitch(0);
  }

  private buildSkyConfig(target: Landable): SurfaceSkyConfig {
    const targetPos = new THREE.Vector3();
    target.getWorldPosition(targetPos);
    const star = this.spaceScene.getStar();
    let sun: SkyBody | undefined;
    if (star) {
      const starPos = new THREE.Vector3();
      star.getWorldPosition(starPos);
      const delta = starPos.clone().sub(targetPos);
      const distance = Math.max(delta.length(), 1);
      delta.divideScalar(distance);
      if (delta.y < 0.15) {
        delta.y = 0.15;
        delta.normalize();
      }
      const size = THREE.MathUtils.clamp(
        (star.radius * 5000) / distance * 0.22,
        220,
        520
      );
      sun = {
        direction: delta,
        color: palette.star.warm,
        glow: palette.star.gold,
        size,
        isSun: true
      };
    }

    const bodies: SkyBody[] = [];
    const delta = new THREE.Vector3();
    const other = new THREE.Vector3();
    for (const body of this.spaceScene.getLandables()) {
      if (body === target) continue;
      body.getWorldPosition(other);
      delta.copy(other).sub(targetPos);
      const distance = delta.length();
      if (distance < 1) continue;
      const dir = delta.clone().divideScalar(distance);
      if (dir.y < -0.1) continue;
      if (dir.y < 0.1) dir.y = 0.1;
      dir.normalize();
      const size = THREE.MathUtils.clamp(
        (body.radius * 5000) / distance * 0.18,
        14,
        95
      );
      bodies.push({
        direction: dir,
        color: body.palette.base,
        glow: body.palette.halo,
        size
      });
    }

    return { sun, bodies };
  }

  private bindStationController(): void {
    // Rebuild on each entry in case astronaut reference changed.
    this.stationFpsController = new FirstPersonController(
      this.stationScene.astronautEntity,
      this.input,
      this.cameraRig.camera,
      this.stationScene.terrainInstance,
      this.stationScene.obstacles,
      undefined,
      this.audio
    );
  }

  private alignStationFpsYawToSpawn(): void {
    if (!this.stationFpsController) return;
    this.stationFpsController.setYaw(this.stationScene.getSpawnFacing());
    this.stationFpsController.setPitch(0);
  }

  private openShop(counter: StationCounter): void {
    this.prompt.hide();
    this.audio.playConfirm();
    this.stationFpsController?.setEnabled(false);
    this.fpsMiningTool?.setEnabled(false);
    if (document.exitPointerLock) document.exitPointerLock();
    this.crosshair.hide();
    this.shopOverlay.show(counter.kind);
    this.missionBus.emit('stationServiceOpened', { service: counter.kind });
  }

  private closeShop(): void {
    this.shopOverlay.hide();
    if (this.mode === 'station-interior') {
      this.stationFpsController?.setEnabled(true);
    }
    this.input.requestPointerLock();
  }

  /** Called by UI when the user clicks the shop's Close button. */
  onShopClosedExternally(): void {
    if (this.mode === 'station-interior') {
      this.stationFpsController?.setEnabled(true);
    }
    this.input.requestPointerLock();
  }

  private openMissions(): void {
    this.prompt.hide();
    this.audio.playConfirm();
    this.stationFpsController?.setEnabled(false);
    if (document.exitPointerLock) document.exitPointerLock();
    this.crosshair.hide();
    this.missionOverlay.show();
    this.missionBus.emit('missionTerminalOpened', {});
  }

  private openSystemMap(): void {
    this.prompt.hide();
    this.audio.playConfirm();
    this.spaceScene.getShipControl()?.setEnabled(false);
    if (document.exitPointerLock) document.exitPointerLock();
    this.crosshair.hide();
    this.systemMapOverlay.show();
    this.missionBus.emit('systemMapOpened', {});
  }

  private closeSystemMap(): void {
    this.systemMapOverlay.hide();
    if (this.mode === 'space-flying') {
      this.spaceScene.getShipControl()?.setEnabled(true);
      this.crosshair.show();
    }
    this.input.requestPointerLock();
  }

  /** Called by the map overlay (or anywhere else) to set the active waypoint. */
  setDestinationById(id: string | null): void {
    if (!id) {
      this.currentDestination = null;
      this.currentDestinationBody = null;
      this.waypointHUD.setTarget(null, this.cameraRig.camera, () =>
        this.spaceScene.getPlayerShip()?.object3D.position ?? new THREE.Vector3()
      );
      return;
    }
    const bodies = this.spaceScene.getMapBodies();
    const body = bodies.find((b) => b.id === id);
    if (!body) {
      this.currentDestination = null;
      this.currentDestinationBody = null;
      this.waypointHUD.setTarget(null, this.cameraRig.camera, () =>
        this.spaceScene.getPlayerShip()?.object3D.position ?? new THREE.Vector3()
      );
      return;
    }
    this.currentDestination = {
      id: body.id,
      name: body.name,
      getWorldPosition: body.getWorldPosition
    };
    this.currentDestinationBody = body;
    this.waypointHUD.setTarget(this.currentDestination, this.cameraRig.camera, () =>
      this.spaceScene.getPlayerShip()?.object3D.position ?? new THREE.Vector3()
    );
    this.missionBus.emit('mapDestinationSelected', { destinationId: body.id });
  }

  private initJumpController(): void {
    const ship = this.spaceScene.getPlayerShip();
    const shipControl = this.spaceScene.getShipControl();
    const stats = this.spaceScene.getStats();
    if (!ship || !shipControl) return;
    const warpStreaks = new WarpStreaks();
    this.jumpController = new JumpController({
      ship,
      shipControl,
      input: this.input,
      stats,
      baseMaxSpeed: gameConfig.playerShip.maxSpeed,
      // Same constant ShipControlSystem uses for normal flight (0.07 fuel/s
      // at full thrust). Hardcoded here to avoid coupling — JumpController
      // multiplies it by FUEL_MULTIPLIER while jumping.
      baseFuelPerSecond: 0.07,
      getDestination: () => this.currentDestinationBody,
      getArrivalRadius: (body) => body.arrivalRadius,
      hud: this.jumpHud,
      audio: this.audio,
      camera: this.cameraRig.camera,
      warpStreaks,
      // Captured by reference — populated lazily by applyCameraForSpaceFlying
      // since the FollowController doesn't exist at construction time.
      snapCamera: () => this.spaceFollowController?.snapToTarget(),
      onChargeStarted: (destinationId) =>
        this.missionBus.emit('jumpChargeStarted', { destinationId }),
      onJumpCompleted: (destinationId) =>
        this.missionBus.emit('hyperspaceJumpCompleted', { destinationId })
    });
  }

  getCurrentDestinationId(): string | null {
    return this.currentDestination?.id ?? null;
  }

  getMissionDestinationId(): string | null {
    return this.missionDestinationBody?.id ?? null;
  }

  /** Resolve the mission's waypoint token. Tokens for space targets
   *  (station, planet/moon names) feed the WaypointHUD's mission slot;
   *  surface-only tokens ('surface-beacon', 'surface-ship') feed the
   *  surface beacon + minimap mission marker. Called whenever the
   *  MissionManager emits a new state. */
  private applyMissionWaypoint(snap: MissionStateSnapshot): void {
    const token = snap.waypointTarget ?? null;
    if (token === this.missionWaypointToken) return;
    this.missionWaypointToken = token;

    // Always clear the space-side marker first; we re-set below when
    // the new token still resolves to a MapBody.
    this.missionDestination = null;
    this.missionDestinationBody = null;
    this.clearSpaceMissionMarker();

    if (!token) {
      this.disposeMissionBeacon();
      return;
    }

    if (this.isSurfaceToken(token)) {
      // Surface token — beacon spawn happens in
      // `refreshSurfaceMissionState` since it depends on the active
      // surface scene being loaded. Don't touch space marker.
      this.refreshSurfaceMissionState();
      return;
    }

    // Space body token. Beacon (if any) is no longer relevant.
    this.disposeMissionBeacon();

    if (token === 'rescue-tug') {
      // Mobile target — there's no MapBody to resolve. The waypoint
      // HUD reads the tug position via a closure each frame; clears
      // automatically when the rescue ends and the tug despawns.
      this.waypointHUD.setMissionTarget(
        {
          id: 'rescue-tug',
          name: 'Recovery Tug',
          getWorldPosition: (out: THREE.Vector3) => {
            const pos = this.rescue.getTugPosition();
            return pos
              ? out.copy(pos)
              : out.copy(this.spaceScene.getStationPosition());
          }
        },
        this.cameraRig.camera,
        () =>
          this.spaceScene.getPlayerShip()?.object3D.position ?? new THREE.Vector3()
      );
      return;
    }

    // Tutorial-block tokens (signal point, tutorial pirate ship,
    // tutorial cargo target). These are handled by dedicated helpers
    // below since their target positions are not MapBodies.
    if (token === 'unwanted-signal') {
      this.installSignalMarker();
      return;
    }
    if (token === 'tutorial-pirate') {
      this.installPirateMarker();
      return;
    }
    if (token === 'cargo-target') {
      this.installCargoTargetMarker();
      return;
    }

    const body = this.resolveMissionBody(token);
    if (!body) return;
    this.missionDestination = {
      id: body.id,
      name: body.name,
      getWorldPosition: body.getWorldPosition
    };
    this.missionDestinationBody = body;
    this.waypointHUD.setMissionTarget(
      this.missionDestination,
      this.cameraRig.camera,
      () =>
        this.spaceScene.getPlayerShip()?.object3D.position ?? new THREE.Vector3()
    );
  }

  private clearSpaceMissionMarker(): void {
    this.waypointHUD.setMissionTarget(null, this.cameraRig.camera, () =>
      this.spaceScene.getPlayerShip()?.object3D.position ?? new THREE.Vector3()
    );
  }

  private isSurfaceToken(token: MissionWaypointToken): boolean {
    return (
      token === 'surface-beacon' ||
      token === 'surface-ship' ||
      token === 'caelis-base'
    );
  }

  private resolveMissionBody(token: MissionWaypointToken): MapBody | null {
    const bodies = this.spaceScene.getMapBodies();
    return bodies.find((b) => b.id === token) ?? null;
  }

  /** Re-evaluate the surface marker state based on the active mission
   *  token + current scene. Called after step changes, after landing,
   *  and after enter/exit-ship transitions so the beacon spawns at
   *  the right moment instead of trying to fire before the surface
   *  scene exists. */
  private refreshSurfaceMissionState(): void {
    const token = this.missionWaypointToken;
    if (!token || !this.isSurfaceToken(token)) {
      this.disposeMissionBeacon();
      return;
    }
    if (this.mode !== 'surface-astronaut' && this.mode !== 'surface-ship') {
      // Off-planet — surface tokens have no visible target. The hint
      // text in the tracker HUD covers the player's "go land somewhere
      // first" reading.
      this.disposeMissionBeacon();
      return;
    }

    if (token === 'surface-beacon') {
      this.ensureMissionBeacon();
    } else {
      this.disposeMissionBeacon();
    }
  }

  private ensureMissionBeacon(): void {
    if (this.missionBeacon) return;
    let shipPos: THREE.Vector3 | null = null;
    try {
      shipPos = this.surfaceScene.shipEntity.object3D.position.clone();
    } catch {
      return; // surface scene not loaded yet
    }
    if (!shipPos) return;
    const angle = Math.random() * Math.PI * 2;
    const baseX = shipPos.x + Math.cos(angle) * this.SURFACE_BEACON_OFFSET;
    const baseZ = shipPos.z + Math.sin(angle) * this.SURFACE_BEACON_OFFSET;
    const groundY = this.surfaceScene.terrainInstance.getCollisionGroundY(
      baseX,
      baseZ
    );
    const beacon = new MissionSurfaceBeacon(
      new THREE.Vector3(baseX, groundY, baseZ)
    );
    this.surfaceScene.root.add(beacon.object3D);
    this.missionBeacon = beacon;
  }

  private disposeMissionBeacon(): void {
    if (!this.missionBeacon) return;
    this.missionBeacon.dispose();
    this.missionBeacon = null;
  }

  private closeMissions(): void {
    this.missionOverlay.hide();
    if (this.mode === 'station-interior') {
      this.stationFpsController?.setEnabled(true);
    }
    this.input.requestPointerLock();
  }

  /** Called by UI when user clicks Close on the mission overlay. */
  onMissionsClosedExternally(): void {
    if (this.mode === 'station-interior') {
      this.stationFpsController?.setEnabled(true);
    }
    this.input.requestPointerLock();
  }

  /** Called by UI when user clicks Close on the system map. */
  onSystemMapClosedExternally(): void {
    if (this.mode === 'space-flying') {
      this.spaceScene.getShipControl()?.setEnabled(true);
      this.crosshair.show();
    }
    this.input.requestPointerLock();
  }

  private applyCameraForStation(): void {
    this.cameraRig.setController(null);
    this.cameraRig.camera.fov = 74;
    this.cameraRig.camera.near = 0.05;
    this.cameraRig.camera.updateProjectionMatrix();
    this.stationFpsController?.syncCamera();
    this.minimap.hide();
    this.crosshair.show();
    this.shipStatsHUD.hide();
    this.astronautStatsHUD.show();
    this.miningToolHUD.hide();
  }

  private bindSurfaceControllers(): void {
    // Defensive: any previous tool's viewmodel + beam already had their
    // owning scene root cleared by `prepareFor`, but the JS reference might
    // still be live. Dispose so we never tick a stale instance.
    this.disposeSurfaceMiningTool();

    // Surface mode opts in to the planet barrier + collision damage; the
    // station interior keeps its barrier-free defaults.
    const fpsConfig = {
      ...DEFAULT_FPS_CONFIG,
      boundaryRadius: PLANET_BARRIER_RADIUS
    };
    const shipConfig = {
      ...DEFAULT_SURFACE_SHIP_CONFIG,
      boundaryRadius: PLANET_BARRIER_RADIUS
    };

    this.fpsController = new FirstPersonController(
      this.surfaceScene.astronautEntity,
      this.input,
      this.cameraRig.camera,
      this.surfaceScene.terrainInstance,
      this.surfaceScene.obstacles,
      fpsConfig,
      this.audio,
      this.surfaceScene.getStats()
    );
    this.surfaceShipController = new SurfaceShipController(
      this.surfaceScene.shipEntity,
      this.input,
      this.surfaceScene.terrainInstance,
      this.surfaceScene.obstacles,
      shipConfig,
      this.audio,
      this.surfaceScene.getStats()
    );

    // FPS mining cutter — instantiated per-landing because the scene root
    // and explosions instance are rebuilt on each `prepareFor`. Disposed in
    // `disposeSurfaceMiningTool` (called on takeoff or destruction).
    const explosions = this.surfaceScene.getExplosions();
    if (explosions) {
      this.fpsMiningTool = new FpsMiningTool(
        this.cameraRig.camera,
        this.input,
        this.shipUpgrades,
        this.fpsInventory,
        explosions,
        this.audio,
        this.surfaceScene.root,
        () => this.surfaceScene.oreDeposits,
        undefined,
        {
          onDepositDestroyed: (deposit, yieldAmount) => {
            this.surfaceScene.removeDeposit(deposit);
            if (yieldAmount > 0) {
              this.missionBus.emit('surfaceDepositMined', {
                count: yieldAmount
              });
            }
          },
          onEquipChange: (equipped) =>
            this.miningToolHUD.setEquipped(equipped)
        }
      );
    }
    this.miningToolHUD.setEquipped(false);
  }

  private disposeSurfaceMiningTool(): void {
    if (!this.fpsMiningTool) return;
    this.fpsMiningTool.dispose();
    this.fpsMiningTool = null;
    this.miningToolHUD.setEquipped(false);
  }

  private applyCameraForSpaceFlying(): void {
    const target = this.spaceScene.getFocusTarget();
    if (!target) return;
    this.cameraRig.camera.fov = 72;
    this.cameraRig.camera.near = 0.1;
    this.cameraRig.camera.updateProjectionMatrix();
    const controller = new FollowController(this.cameraRig.camera, target, {
      ...gameConfig.camera.follow,
      // Pull the camera tighter when the throttle is up so the ship
      // dominates the frame in motion; eases back out at rest.
      closeOffsetBehind: 11,
      // Override the gentle defaults — ship cruise speed plus the dynamic
      // close-offset means the lerp must track quickly, otherwise the
      // camera trails ~26u behind the desired position and the ship looks
      // much further away during normal flight than it does mid-jump
      // (which already snaps directly to target).
      positionLerp: 100,
      lookLerp: 100,
      // Drive the offset transition off the ship's actual velocity rather
      // than its raw thrust input. Thrust flips 0↔1 instantly when the
      // pilot taps W, which yanks the camera. Velocity ramps via the
      // ship's acceleration / damping, so the offset eases between the
      // resting and close values over the same ~2 s the ship needs to
      // reach cruise speed. The jump-active override keeps the snap at
      // close offset — the jump zeroes ship.velocity but should still
      // frame the ship tight.
      getCloseFactor: () => {
        if (this.jumpController?.isActive()) return 1;
        const ship = this.spaceScene.getPlayerShip();
        if (!ship) return 0;
        return Math.min(
          ship.velocity.length() / gameConfig.playerShip.maxSpeed,
          1
        );
      }
    });
    this.spaceFollowController = controller;
    this.cameraRig.setController(controller);
    this.minimap.show();
    this.crosshair.show();
    this.shipStatsHUD.show();
    this.astronautStatsHUD.hide();
    this.miningToolHUD.hide();
  }

  private applyCameraForAstronaut(): void {
    this.cameraRig.setController(null);
    this.cameraRig.camera.fov = 74;
    this.cameraRig.camera.near = 0.05;
    this.cameraRig.camera.updateProjectionMatrix();
    this.fpsController?.syncCamera();
    this.minimap.show();
    this.crosshair.hide();
    this.shipStatsHUD.hide();
    this.astronautStatsHUD.show();
    this.miningToolHUD.show();
  }

  private applyCameraForSurfaceShip(): void {
    this.cameraRig.camera.fov = 72;
    this.cameraRig.camera.near = 0.1;
    this.cameraRig.camera.updateProjectionMatrix();
    this.cameraRig.setController(
      new FollowController(
        this.cameraRig.camera,
        this.surfaceScene.shipEntity.object3D,
        gameConfig.camera.follow
      )
    );
    this.minimap.hide();
    this.crosshair.show();
    this.shipStatsHUD.show();
    this.astronautStatsHUD.hide();
    this.miningToolHUD.hide();
  }

  /** Tick the space-side return portal (only present when the player
   *  arrived via Vibejam) and redirect when the ship flies into it. */
  private updateStartPortal(dt: number): void {
    const portal = this.spaceScene.getStartPortal();
    if (!portal) return;
    portal.update(dt, this.cameraRig.camera.position);
    if (this.portalRedirecting) return;
    const ship = this.spaceScene.getPlayerShip();
    if (!ship) return;
    if (!portal.containsPoint(ship.object3D.position)) return;
    const ctx = this.portalContext;
    if (!ctx) return;
    const url = buildReturnUrl(ctx);
    if (!url) return;
    this.portalRedirecting = true;
    window.location.href = url;
  }

  /** Tick the station-interior Vibejam exit portal and redirect when
   *  the player walks through the archway trigger. */
  private updateStationVibejamPortal(): void {
    if (this.portalRedirecting) return;
    const portal = this.stationScene.getVibejamPortal();
    if (!portal) return;
    const astronaut = this.stationScene.astronautEntity;
    if (!portal.containsPoint(astronaut.object3D.position)) return;
    const stats = this.spaceScene.getStats();
    const ship = this.spaceScene.getPlayerShip();
    const speed = ship?.velocity.length() ?? 0;
    const username = this.portalContext?.username ?? null;
    const color = this.portalContext?.color ?? null;
    const url = buildVibejamExitUrl({
      username,
      color,
      speed,
      // Forward HP and any other live continuity data.
      extra: new Map<string, string>([
        ['hp', String(Math.max(1, Math.round(stats.health)))]
      ])
    });
    this.portalRedirecting = true;
    window.location.href = url;
  }

  /** Per-frame state checks for the long-range tutorial block —
   *  alignment with the current destination (mission 9), fuel-out
   *  edge detection (mission 10), and the scripted "autonomy test"
   *  fuel drain that kicks in when the matching step is active.
   *
   *  All checks are guarded so they're cheap when the player isn't
   *  in a step that cares about them. */
  private updateLongRangeTutorial(dt: number): void {
    const ship = this.spaceScene.getPlayerShip();
    if (!ship) return;
    const stats = this.spaceScene.getStats();

    // Alignment: edge-triggered emit when the ship's nose enters the
    // jump facing cone of the active destination. Resets the moment
    // the player turns away or swaps destination.
    const dest = this.currentDestinationBody;
    if (dest) {
      _tmpAlignA.set(0, 0, -1).applyQuaternion(ship.object3D.quaternion);
      dest.getWorldPosition(_tmpAlignB);
      _tmpAlignB.sub(ship.object3D.position).normalize();
      const aligned = _tmpAlignA.dot(_tmpAlignB) >= JUMP_FACING_THRESHOLD;
      if (aligned && this.jumpAlignedDestId !== dest.id) {
        this.jumpAlignedDestId = dest.id;
        this.missionBus.emit('jumpTargetAligned', { destinationId: dest.id });
      } else if (!aligned && this.jumpAlignedDestId === dest.id) {
        this.jumpAlignedDestId = null;
      }
    } else if (this.jumpAlignedDestId !== null) {
      this.jumpAlignedDestId = null;
    }

    // Autonomy-test fuel drain — runs only while the matching step is
    // active. Drain rate is calibrated so a player at the start of
    // the burn is empty in ~12 seconds even with idle thrust, so the
    // tutorial beat doesn't drag on.
    if (this.activeStepId === AUTONOMY_BURN_STEP_ID && stats.fuel > 0) {
      stats.consumeFuel(stats.fuelMax * 0.085 * dt);
    }

    // Fuel-out edge detection. Edge-triggered so the bus only fires
    // once per drain. Resetting on refuel means a future depletion
    // re-fires cleanly.
    const empty = stats.fuel <= 0;
    if (empty && !this.prevFuelLow) {
      this.prevFuelLow = true;
      this.missionBus.emit('fuelDepleted', {});
    } else if (!empty && this.prevFuelLow) {
      this.prevFuelLow = false;
    }

    // Refuel edge detection — emits the moment fuel crosses the
    // post-rescue safe threshold so the "Refuel Before Trouble" step
    // can complete on a single signal.
    const ratio = stats.fuelMax > 0 ? stats.fuel / stats.fuelMax : 0;
    const refilled = ratio >= GameDirector.REFUEL_TARGET_RATIO;
    if (refilled && !this.prevFuelFull) {
      this.prevFuelFull = true;
      this.missionBus.emit('fuelRefilled', { fuelRatio: ratio });
    } else if (!refilled && this.prevFuelFull) {
      this.prevFuelFull = false;
    }

    this.tickMissionProximity(ship.object3D.position);
  }

  /** Edge-triggered "player reached the active mission target" emit.
   *  Fires once per token activation when the player gets inside the
   *  reach radius for that token type. Re-arms the moment the
   *  active token swaps. */
  private tickMissionProximity(playerPos: THREE.Vector3): void {
    const token = this.missionWaypointToken;
    if (!token) {
      this.missionTargetReachedToken = null;
      return;
    }
    if (this.missionTargetReachedToken === token) return;

    const targetPos = this.resolveTokenPosition(token);
    if (!targetPos) return;
    const reach = this.proximityReachForToken(token);
    if (playerPos.distanceToSquared(targetPos) > reach * reach) return;
    this.missionTargetReachedToken = token;
    this.missionBus.emit('missionTargetReached', { token });
  }

  private proximityReachForToken(token: MissionWaypointToken): number {
    // Planet bodies use their arrival radius (already broad — covers
    // ring + belt). Other tokens use a tutorial-friendly default.
    const body = this.resolveMissionBody(token);
    if (body) return body.arrivalRadius * 1.05;
    if (token === 'unwanted-signal') return 90;
    if (token === 'tutorial-pirate') return 240;
    // Cargo target uses a wider reach: the marker icon is sized to
    // be readable at long range, so once the player closes inside
    // ~360u the icon would otherwise sit on top of the actual ship
    // silhouette. Closing the locate step earlier lets the intercept
    // step (no marker) take over while the ship is still visible.
    if (token === 'cargo-target') return 360;
    if (token === 'rescue-tug') return 60;
    return 80;
  }

  /** Resolve the world-space position of an arbitrary mission token —
   *  whether it's a planet, station, fixed signal, mobile NPC or the
   *  rescue tug. Returns null when the target doesn't exist (e.g.
   *  ship was destroyed or signal wasn't computed yet). */
  private resolveTokenPosition(token: MissionWaypointToken): THREE.Vector3 | null {
    if (token === 'unwanted-signal') {
      return this.tutorialSignalPos?.clone() ?? null;
    }
    if (token === 'tutorial-pirate') {
      const ship = this.tutorialPirate;
      return ship && ship.isAlive ? ship.object3D.position.clone() : null;
    }
    if (token === 'cargo-target') {
      const ship = this.tutorialCargoTarget;
      return ship && ship.isAlive ? ship.object3D.position.clone() : null;
    }
    if (token === 'rescue-tug') return this.rescue.getTugPosition();
    const body = this.resolveMissionBody(token);
    if (!body) return null;
    const out = new THREE.Vector3();
    body.getWorldPosition(out);
    return out;
  }

  /** Compute the signal point near Ambre once and cache it. The
   *  signal sits at a fixed offset from Ambre's *current* world
   *  position so it's always close to the planet without colliding
   *  with the ring. */
  private ensureSignalPoint(): THREE.Vector3 | null {
    if (this.tutorialSignalPos) return this.tutorialSignalPos;
    const ambre = this.spaceScene
      .getMapBodies()
      .find((b) => b.id === 'Ambre');
    if (!ambre) return null;
    const center = new THREE.Vector3();
    ambre.getWorldPosition(center);
    // Park the signal point ~60u above Ambre's orbital plane and
    // 200u radially out from its current position — well clear of
    // the ring/atmosphere and easy to reach without a precision
    // approach.
    const radial = center.clone();
    radial.y = 0;
    if (radial.lengthSq() < 1) radial.set(1, 0, 0);
    radial.normalize();
    this.tutorialSignalPos = center
      .clone()
      .addScaledVector(radial, ambre.arrivalRadius * 0.55)
      .add(new THREE.Vector3(0, 60, 0));
    return this.tutorialSignalPos;
  }

  private installSignalMarker(): void {
    const pos = this.ensureSignalPoint();
    if (!pos) return;
    this.waypointHUD.setMissionTarget(
      {
        id: 'unwanted-signal',
        name: 'Hostile Signal',
        getWorldPosition: (out: THREE.Vector3) => {
          const live = this.tutorialSignalPos;
          return live ? out.copy(live) : out.copy(pos);
        }
      },
      this.cameraRig.camera,
      () =>
        this.spaceScene.getPlayerShip()?.object3D.position ?? new THREE.Vector3()
    );
  }

  /** Spawn the tutorial pirate at the cached signal point and switch
   *  the marker to track it. Idempotent — re-installing on the same
   *  step doesn't re-spawn. */
  private installPirateMarker(): void {
    if (!this.tutorialPirate || !this.tutorialPirate.isAlive) {
      const npcDir = this.spaceScene.getNpcDirector();
      const spawnAt = this.tutorialSignalPos ?? this.ensureSignalPoint();
      if (npcDir && spawnAt) {
        this.tutorialPirate = npcDir.spawnTutorialPirate(spawnAt);
        // Face the player on spawn so the brain's first frame
        // already passes the fire-cone check — the pirate opens
        // fire immediately instead of needing a turn-around tick.
        const playerShip = this.spaceScene.getPlayerShip();
        if (playerShip) {
          this.tutorialPirate.object3D.lookAt(playerShip.object3D.position);
        }
      }
    }
    const pirate = this.tutorialPirate;
    if (!pirate) return;
    this.waypointHUD.setMissionTarget(
      {
        id: 'tutorial-pirate',
        name: 'Pirate Ship',
        getWorldPosition: (out: THREE.Vector3) => {
          if (!this.tutorialPirate || !this.tutorialPirate.isAlive) {
            return out.copy(pirate.object3D.position);
          }
          return out.copy(this.tutorialPirate.object3D.position);
        }
      },
      this.cameraRig.camera,
      () =>
        this.spaceScene.getPlayerShip()?.object3D.position ?? new THREE.Vector3()
    );
  }

  /** Spawn a dedicated cargo hauler near the player and lock the
   *  marker on it. Reuses the standard miner brain with an
   *  effectively-infinite quota AND the mission-protected flag, so
   *  the ship neither finishes its work cycle nor gets despawned
   *  while the player is still hunting it.
   *
   *  The marker projects ABOVE the ship's health bar (and renders in
   *  compact size) so the player can keep eyes on the silhouette
   *  while the hauler tries to flee. */
  private installCargoTargetMarker(): void {
    if (!this.tutorialCargoTarget || !this.tutorialCargoTarget.isAlive) {
      this.tutorialCargoTarget = this.spawnTutorialCargoMiner();
    }
    const target = this.tutorialCargoTarget;
    if (!target) return;
    // Health bar sits at `collisionRadius + 1.6` (see NpcDirector).
    // Add a comfortable gap so the marker doesn't visually crash into
    // the bar.
    const markerOffsetY = target.collisionRadius + 3.4;
    this.waypointHUD.setMissionTarget(
      {
        id: 'cargo-target',
        name: 'Cargo Hauler',
        compact: true,
        getWorldPosition: (out: THREE.Vector3) => {
          const live =
            this.tutorialCargoTarget && this.tutorialCargoTarget.isAlive
              ? this.tutorialCargoTarget
              : target;
          out.copy(live.object3D.position);
          out.y += markerOffsetY;
          return out;
        }
      },
      this.cameraRig.camera,
      () =>
        this.spaceScene.getPlayerShip()?.object3D.position ?? new THREE.Vector3()
    );
  }

  /** Pick a spawn position a comfortable distance ahead of the
   *  player and call the director's protected-spawn helper. Falls
   *  back to null if the scene isn't ready (e.g. boot-time edge). */
  private spawnTutorialCargoMiner(): import('../npcs').NpcShip | null {
    const ship = this.spaceScene.getPlayerShip();
    const npcDir = this.spaceScene.getNpcDirector();
    if (!ship || !npcDir) return null;
    // Place the cargo hauler ~900u ahead of the player along their
    // current heading so the player has time to spot the marker and
    // commit to the intercept. Heading falls back to +Z when the
    // ship is stationary so the spawn is always a sensible distance
    // away.
    const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(
      ship.object3D.quaternion
    );
    if (forward.lengthSq() < 1e-4) forward.set(0, 0, 1);
    forward.normalize();
    const spawn = ship.object3D.position
      .clone()
      .addScaledVector(forward, 900)
      .add(new THREE.Vector3(0, 40, 0));
    return npcDir.spawnTutorialCargoMiner(spawn);
  }

  /** Hook installed on MainScene — runs once per NPC death. Fires
   *  mission events for tagged tutorial targets so the mission system
   *  can advance, and credits special loot for the tutorial pirate. */
  private handleNpcDestroyed(npc: import('../npcs').NpcShip): void {
    if (this.tutorialPirate && npc === this.tutorialPirate) {
      // Pirate-specific tutorial loot — a guaranteed mid-tier drop so
      // the player sees real value instead of trash. Credited to the
      // ship inventory (already the standard target for asteroid /
      // miner loot in this scene).
      this.spaceScene.getStats(); // ensure scene access path stays warm
      const piratePayout: Array<{ readonly itemId: string; readonly count: number }> = [
        { itemId: 'silver', count: 4 },
        { itemId: 'plasma-crystal', count: 1 }
      ];
      for (const drop of piratePayout) {
        this.spaceScene
          .getShipInventoryRef()
          .addItem(drop.itemId as never, drop.count);
      }
      this.missionBus.emit('missionShipDestroyed', { missionTag: 'tutorial-pirate' });
      this.missionBus.emit('missionItemCollected', { kind: 'pirate-loot' });
      this.tutorialPirate = null;
      return;
    }
    if (this.tutorialCargoTarget && npc === this.tutorialCargoTarget) {
      // Cargo-target uses the shared miner loot drop already credited
      // by MainScene's combat hook — no extra loot here. Just emit
      // the mission events.
      this.missionBus.emit('missionShipDestroyed', { missionTag: 'cargo-target' });
      this.missionBus.emit('missionItemCollected', { kind: 'cargo' });
      this.tutorialCargoTarget = null;
      return;
    }
  }
}

const JUMP_FACING_THRESHOLD = 0.92; // matches JumpController's check
const AUTONOMY_BURN_STEP_ID = 'autonomy-test-burn';
const _tmpAlignA = new THREE.Vector3();
const _tmpAlignB = new THREE.Vector3();

function hexToCss(hex: number): string {
  const r = (hex >> 16) & 0xff;
  const g = (hex >> 8) & 0xff;
  const b = hex & 0xff;
  return `rgb(${r},${g},${b})`;
}
